import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSession } from "@jkast90/kast-common/auth";
import { displayName, romBaseName } from "../emulator/saves";
import { signedInAdmin } from "../library/admin";
import Cover from "../library/Cover";
import { useCoverIndex, withCoversFirst } from "../library/covers";
import type { CloudRom } from "../library/cloudLibrary";
import { topGames, topPlayers, type Feed } from "../library/dashboard";
import PlayerLink from "../library/PlayerLink";
import { buildLibrary, bySystem, type LibraryGame } from "../library/myLibrary";
import { formatPlayed } from "../library/playtime";
import { useCloudRoms, useDashboard, useMyPlaytime } from "../library/queries";
import { errorText } from "@jkast90/kast-common/api-client";
import { count } from "@jkast90/kast-common/format";
import { Notice, RankedList } from "@jkast90/kast-common/ui";
import { followRenames } from "../library/renames";
import { here, isDesktop } from "../desktop/desktop";
import { listLocalGames, SYSTEM_LABEL, type LocalGame, type System } from "../library/localLibrary";
import { signIn } from "../auth/session";
import { useOnline } from "@jkast90/kast-common/offline";

// Stable, so the rename effect below does not rerun on every render.
const NO_ROMS: CloudRom[] = [];

const TOP = 5;

// Everyone's play time, two ways: the games with the most hours on them,
// each opening in the library, and the players with the most.
function PlayWidgets({ feed }: { feed: Feed }) {
  const me = signedInAdmin().sub;
  const games = topGames(feed, TOP);
  const players = topPlayers(feed, TOP);
  if (!games.length) return null;
  return (
    <div className="home-widgets">
      <section className="panel">
        <h2>Most played</h2>
        <RankedList
          aria-label="Most played"
          items={games.map((game) => ({
            key: game.game,
            leading: <Cover name={game.name} className="rank-cover" />,
            title: (
              <Link to={`/library?${new URLSearchParams({ game: romBaseName(game.name) })}`}>
                {displayName(game.name)}
              </Link>
            ),
            meta: `${SYSTEM_LABEL[game.system as System] ?? game.system} · ${count(game.players.length, "player")}`,
            value: formatPlayed(game.seconds),
          }))}
        />
      </section>
      <section className="panel">
        <h2>Leaderboard</h2>
        <RankedList
          aria-label="Leaderboard"
          items={players.map((player) => ({
            key: player.sub,
            title: <PlayerLink sub={player.sub} email={player.email} name={player.name} me={me} />,
            meta: count(player.played.length, "game"),
            value: formatPlayed(player.secondsPlayed),
            highlight: player.sub === me,
          }))}
        />
      </section>
    </div>
  );
}

// One console: covers of its most recent games, its drawing beside its
// name and game count, and a way into the library filtered to it.
function ConsoleTile({ system, games }: { system: System; games: LibraryGame[] }) {
  const here = games.filter((g) => g.local).length;
  const strip = useRef<HTMLSpanElement>(null);
  // Games with box art make the strip; the rest fill in only when needed.
  const shown = withCoversFirst(games, useCoverIndex()).slice(0, 3);
  // A game without a cover gets a blank the size of the covers beside it,
  // on average, so the strip reads as one row; measured as they load.
  useEffect(() => {
    const el = strip.current;
    if (!el) return;
    const measure = () => {
      const sizes = [...el.querySelectorAll<HTMLImageElement>("img.console-cover")].filter((img) => img.naturalWidth);
      if (!sizes.length) return;
      const width = sizes.reduce((sum, img) => sum + img.getBoundingClientRect().width, 0) / sizes.length;
      const height = sizes.reduce((sum, img) => sum + img.getBoundingClientRect().height, 0) / sizes.length;
      el.style.setProperty("--blank-width", `${Math.round(width)}px`);
      el.style.setProperty("--blank-height", `${Math.round(height)}px`);
    };
    const sizes = new ResizeObserver(measure);
    const watch = () => {
      sizes.disconnect();
      for (const img of el.querySelectorAll("img.console-cover")) sizes.observe(img);
      measure();
    };
    const children = new MutationObserver(watch);
    children.observe(el, { childList: true, subtree: true });
    watch();
    return () => {
      sizes.disconnect();
      children.disconnect();
    };
  }, [games]);
  return (
    <li>
      <Link to={`/library?${new URLSearchParams({ system })}`} className="console-tile">
        <span className="console-covers" aria-hidden="true" ref={strip}>
          {shown.map((game) => (
            <Cover key={game.key} name={game.name} className="console-cover" />
          ))}
        </span>
        <span className="console-title">
          <img src={`/consoles/${system}.png`} alt="" className="console-icon" />
          <span className="console-text">
            <strong>{SYSTEM_LABEL[system]}</strong>
            <span className="kui-muted kui-small">
              {count(games.length, "game")}
              {here === 0 ? " · in the cloud" : here < games.length ? ` · ${here} on this device` : ""}
            </span>
          </span>
        </span>
      </Link>
    </li>
  );
}

export default function HomePage() {
  const { search } = useLocation();
  const isAuthenticated = useSession().status === "signedIn";
  const online = useOnline();
  const [local, setLocal] = useState<LocalGame[] | null>(null);
  const cloudRoms = useCloudRoms(isAuthenticated);
  // A disabled query still hands back what it cached; signed out, show none of it.
  const cloud = (isAuthenticated && cloudRoms.data) || NO_ROMS;
  const playData = useMyPlaytime(isAuthenticated).data;
  const plays = isAuthenticated ? playData : undefined;
  const feedData = useDashboard(isAuthenticated).data;
  const feed = isAuthenticated ? feedData : undefined;

  useEffect(() => {
    listLocalGames().then(setLocal, () => setLocal([]));
  }, []);

  // A library game an admin renamed takes this browser's copy, saves and
  // slots along to its new name.
  useEffect(() => {
    if (!local?.length || !cloud.length) return;
    followRenames(local, cloud).then(
      (moved) => {
        if (moved) listLocalGames().then(setLocal, () => {});
      },
      () => {},
    );
  }, [local, cloud]);

  const consoles = useMemo(
    () =>
      local &&
      bySystem(
        buildLibrary({
          local,
          cloudRoms: cloud,
          plays: plays ?? [],
          cloudSaves: [],
          mgbaSaves: [],
          ejsSaves: new Map(),
          ejsStates: [],
        }),
      ),
    [local, cloud, plays],
  );

  // Old links pointed the player at the root. A full page load: only /play
  // is served cross-origin isolated.
  if (new URLSearchParams(search).has("game")) {
    window.location.replace(`/play${search}`);
    return null;
  }

  return (
    <div className="home-main">
      <div className="home-top">
        <section className="hero">
          <img src="/icons/icon-192.png" alt="" className="hero-icon" />
          <h1>CloudBoy</h1>
          <p className="kui-muted">
            {isDesktop
              ? "Game Boy, Game Boy Advance, Nintendo 64, PlayStation, PSP and Xbox on your Mac, with your saves in the cloud."
              : "Game Boy, Game Boy Color, Game Boy Advance and Nintendo 64, in the browser, with your saves in the cloud."}
          </p>
        </section>
      </div>

      {cloudRoms.error && (
        <Notice tone="error" style={{ marginBottom: "1rem" }}>
          {errorText(cloudRoms.error, "Could not load the cloud library.")}
        </Notice>
      )}
      {!isAuthenticated &&
        (online ? (
          <p className="kui-muted kui-small cloud-hint">
            Games in your cloud library show here once you{" "}
            <button type="button" className="link" onClick={signIn}>
              sign in
            </button>
            .
          </p>
        ) : (
          <p className="kui-muted kui-small cloud-hint">
            Games in your cloud library show here once you're online and signed in.
          </p>
        ))}

      {consoles === null ? (
        <p className="kui-muted">{isDesktop ? "Looking for games…" : `Looking for games in ${here}…`}</p>
      ) : consoles.length === 0 ? (
        <section className="panel">
          <h2>Library</h2>
          <p className="kui-muted">
            {isAuthenticated ? "No games yet" : `No games on ${here} yet`}. Add one you own, or scan a folder of them,
            in the <Link to="/library">library</Link>.
          </p>
        </section>
      ) : (
        <ul className="kui-list-reset console-tiles">
          {consoles.map(([system, games]) => (
            <ConsoleTile key={system} system={system} games={games} />
          ))}
        </ul>
      )}

      {feed && <PlayWidgets feed={feed} />}
    </div>
  );
}
